<template>
  <main>
    <!-- Navigation and Sidebar -->
    <section
      class="layout-wrapper"
      :class="{'layout-wrapper-mobile': currentStep !== totalSteps}"
    >
      <!--Registration Progress on Mobile (Portrait)-->
      <registration-steps-mobile
        @closeRegMenu="closeRegMenu">
      </registration-steps-mobile>

      <header class="nav" :class="{'nav-padding-height': contentPadding, 'has-disclaimer' : showDemoDisclaimer}">

        <!-- BG color for hambuger and nav brand image-->
        <div class="sidebar-header" :class="{'sidebar-header-collapsed': !sidebarExpanded, 'sidebar-header-padding': contentPadding}"></div>

        <!-- Hamburger -->
        <div class="hidden-sidebar-header" :class="{'hidden-sidebar-header-collapsed': !sidebarExpanded}">
          <div
            class="hamburger"
            @click="toggleSidebar"
            :class="{
              'is-active': isActive,
              'arrow-tooltip--before': true,
              'hamburger-border' : hasScheduledPayments !== null && !hasScheduledPaymentsGotIt && !hasEventOrVisitationsGotIt && hasEventOrVisitations !== null && $route.name === 'Dashboard'
            }"
          >
            <div :class="{'blink' : hasScheduledPayments !== null && !hasScheduledPaymentsGotIt && !hasEventOrVisitationsGotIt && hasEventOrVisitations !== null && $route.name === 'Dashboard'}"></div>
            <span class="line"></span>
            <span class="line"></span>
            <span class="line"></span>
          </div>
          <router-link :to="{ name: isAppLocked ? '' : 'Dashboard' }">
            <div class="logo"></div>
          </router-link>
        </div>

        <f-p-registration-progress-tooltip
            v-if="hasScheduledPayments !== null && !hasScheduledPaymentsGotIt && !hasEventOrVisitationsGotIt && hasEventOrVisitations !== null && $route.name === 'Dashboard' && totalSteps > 1"
            :class="{
                'arrow-tooltip--before': hasScheduledPayments === 'current' || hasEventOrVisitations === 'current',
                'sidebar-unique-tooltip' : true
              }"
            style="top: calc((100% - 100px) / 2); left: 100%;"
            :text="$lang.messages.registration_progress_schedule_payment_calendar"
        >
          <div class="fp-registration-progress-tooltip-button">
            <button @click="gotItClicked">
              {{ $lang.messages.registration_progress_btn_got_it_1 }}
            </button>
          </div>
        </f-p-registration-progress-tooltip>

        <!-- Sub Menu -->
        <div class="sub-nav-menu" :class="{'sub-nav-menu-collapsed': !sidebarExpanded}" ref="subNavMenu">
          <!-- User info dropdown menu -->
          <user-info-menu @toggleProfileMenu="toggleProfileMenu"></user-info-menu>

          <div class="vertical-line user"></div>

          <notifications
              :handleCloseAsideOnNotifs="handleCloseAsideOnNotifs">
          </notifications>

          <div v-if="currentStep !== totalSteps" class="vertical-line"></div>

          <registration-steps></registration-steps>
          <demo-disclaimer v-if="showDemoDisclaimer" :classProp="'demo-disclaimer'"></demo-disclaimer>
        </div>
        
        <demo-disclaimer v-if="showDemoDisclaimer" :classProp="'demo-disclaimer-mobile'"></demo-disclaimer>
      </header>

      <aside
        class="sidebar"
        :class="{
          'sidebar-collapsed': !sidebarExpanded,
          'sidebar-padding': contentPadding,
          'no-reg-steps': currentStep === totalSteps
        }"
      >
        <sidebar
            :sidebarExpanded="sidebarExpanded"
            :toggleSidebar="toggleSidebar"
            ref="sidebar">
        </sidebar>
      </aside>

    </section>

    <!-- Main Content -->
    <section
      class="clearfixing content"
      :class="{'content-collapsed': !sidebarExpanded, 'content-padding-header': contentPadding, 'has-disclaimer' : showDemoDisclaimer}"
    >
      <router-view :sidebarExpandedProp="sidebarExpanded"></router-view>
    </section>

    <f-p-fab
      class="fp-fab"
      :fabbableParent="fabbable"
      v-on:toggle-fab="toggleFab"
      v-click-outside="onClickOutsideFab">
    </f-p-fab>
  </main>
</template>

<script>
  import Sidebar from "@/components/appLayout/Sidebar";
  import { mapGetters, mapActions, mapMutations } from "vuex";
  import {
    GET_RELATED_COPARENTS,
    GET_CASES, GET_MESSAGE_CONTACTS,
    SET_SUBSCRIBED_CONVERSATIONS_LIST,
    REGISTRATION_PROGRESS_GOT_IT_TOOLTIPS,
    GET_REGISTRATION_PROGRESS
  } from "@/store/action.types";
  import {
    SET_CASES,
    SET_CONTACTS_LIST,
    SET_CONVERSATIONS_LIST
  } from "@/store/mutation.types";
  import Notifications from "@/components/common/Notifications";
  import FPFab from "@/components/common/FPFab";
  import RegistrationStepsMobile from "./RegistrationStepsMobile";
  import RegistrationSteps from "./RegistrationSteps";
  import vClickOutside from 'v-click-outside';
  import SockJS from "sockjs-client";
  import Stomp from "webstomp-client";
  import {
    WS,
    WS_ERRORS,
    WS_CONVERSATIONS_SEND,
    WS_CONVERSATIONS_SUBSCRIBE,
    WS_APP,
  } from '@/constants/ApiConstants'
  import FPRegProgressTooltip from '@/components/common/FPRegistrationProgressTooltip';
  import { API_ENV_NAME } from '@/common/config';
  import DemoDisclaimer from "./DemoDisclaimer";
  import STATUS_CODES from "@/common/statusCodes";

  export default {
    name: "AppLayout",
    components: {
      FPFab,
      Notifications,
      Sidebar,
      'registration-steps-mobile': RegistrationStepsMobile,
      'user-info-menu': () => import('./UserInfoMenu'),
      'registration-steps': RegistrationSteps,
      'f-p-registration-progress-tooltip': FPRegProgressTooltip,
      'demo-disclaimer' : DemoDisclaimer
    },
    directives: {
      clickOutside: vClickOutside.directive,
      ClickOutsideRegistration: vClickOutside.directive
    },
    data() {
      return {
        sidebarExpanded: document.documentElement.clientWidth > 1280,
        isActive: false,
        fabbable: true,
        contentPadding: false,
        socketConnected: false,
        conversationsList: [],
        postponedRequestsLoaded: false,
        showDemoDisclaimer: API_ENV_NAME === 'demo'
      };
    },
    computed: {
      ...mapGetters([
        "user",
        "isEmailConfirmed",
        "hasCoparent",
        "hasChildren",
        "hasRecordKeepingCoparents",
        "canSendFunds",
        "isSubscribed",
        "coparentGuid",
        "isAppLocked",
        'currentStep',
        'totalSteps',
        "hasScheduledPayments",
        "hasScheduledPaymentsGotIt",
        "hasEventOrVisitations",
        "hasEventOrVisitationsGotIt",
      ]),
      contactsList() {
        return this.$store.getters.getContactsList
      },
      subscribedConversationsList() {
        return this.$store.getters.getSubscribedConversationsList
      }
    },
    created() {
      if (!this.isAppLocked) {
        this.loadContacts();
        this.loadCases();
      }
      this.loadCoparents();
      // DO NOT DELETE THIS IS USED FOR THEME SELECTION
      // if (window.matchMedia('screen and (prefers-color-scheme: dark)') && !localStorage.getItem('theme')) {
      //     document.body.setAttribute('theme', 'dark');
      // }
      // else {
      //     document.body.setAttribute('theme', localStorage.getItem('theme'));
      // }
    },
    watch: {
      isAppLocked: function(val) {
        if (!val && !this.postponedRequestsLoaded) {
          this.loadContacts();
          this.loadCases();
          this.postponedRequestsLoaded = true;
        }
      }
    },
    mounted() {
      this.connectFpAppWebsocket()
      this.$root.$on('conversations', this.conversationsUpdated);
      this.$root.$on('reload-coparents', this.loadContacts);
      this.handleContentPadding();
      window.addEventListener('resize', this.getWindowWidth);
      window.addEventListener('orientationchange', this.handleOrientationHamburger);
      window.addEventListener('storage', (e) => {
        if (e.key === 'user' || e.key === 'hasCoparent' || e.key === 'hasChildren' || e.key === 'hasRecordKeepingCoparents' || e.key === 'canSendFunds' || e.key === 'isSubscribed') {
          if (e.oldValue !== '') {
            location.reload();
          }
        }
      });
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.getWindowWidth);
      window.removeEventListener('orientationchange', this.handleOrientationHamburger);
      this.disconnectWebsocket();
      this.disconnectFpAppWebsocket();
    },
    methods: {
      ...mapActions({
        getCoparentsAction: GET_RELATED_COPARENTS,
        getCasesAction: GET_CASES,
        getMessageContacts: GET_MESSAGE_CONTACTS,
        setSubscribedConversationsListAction: SET_SUBSCRIBED_CONVERSATIONS_LIST,
        registrationProgresssGotItTooltipsAction: REGISTRATION_PROGRESS_GOT_IT_TOOLTIPS,
        getRegistrationProgressAction: GET_REGISTRATION_PROGRESS
      }),
      ...mapMutations({
        casesMutation: SET_CASES,
        contactsListMutation: SET_CONTACTS_LIST,
        conversationsListMutation: SET_CONVERSATIONS_LIST
      }),
      onClickOutsideFab() {
        this.fabbable = true;
      },
      toggleFab(val) {
        this.fabbable = val;
      },
      handleOrientationHamburger() {
        this.isActive = false;
      },
      getWindowWidth() {
        this.sidebarExpanded = document.documentElement.clientWidth > 1280;
      },
      handleContentPadding() {
        if (this.user.firstname.length + this.user.lastname.length > 17) {
          this.contentPadding = !this.contentPadding
        }
      },
      handleCloseAsideOnNotifs() {
        this.handleOrientationHamburger()
        this.sidebarExpanded = false
      },
      toggleSidebar() {
        this.sidebarExpanded = !this.sidebarExpanded
        this.isActive = !this.isActive
      },
      toggleProfileMenu() {
        // On tablet and below when aside menu is open,
        // close the aside menu when user clicks on
        // registration steps or user profile
        if (window.innerWidth <= 1024) {
          this.isActive = false
          this.sidebarExpanded = false
        }
      },
      closeRegMenu(value) {
        // On tablet and below when aside menu is open,
        // close the aside menu when user clicks on
        // registration steps
        if (window.innerWidth <= 1024 && value) {
          this.isActive = false
          this.sidebarExpanded = false
        }
      },
      loadCoparents() {
        this.getCoparentsAction()
        .then(() => {})
        .catch(error => {
          if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
            this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
          }
        })
      },
      loadCases() {
        this.getCasesAction()
        .then(() => {})
        .catch(error => {
          if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
            this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
          }
        })
      },
      loadContacts() {
        if (!this.isAppLocked) {
          this.getMessageContacts()
          .then(response => {
            this.contactsListMutation(response.data.data)
            // Connect on socket after loading contacts
            this.connectWebsocket();
          })
          .catch(error => {
            if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
              this.$snotify.error(error.message, 'ERROR');
            }
          });
        }
      },
      // Connecting on websocket (MS)
      connectWebsocket() {
        this.socket = new SockJS(WS);
        this.stompClient = Stomp.over(this.socket);
        this.stompClient.debug = () => {};
        this.stompClient.connect({},
          frame => {
            this.socketConnected = true;
            this.subscribeErrors();
            this.subscribeWebsocket();
          },
          error => {
            this.socketConnected = false;
          }
        );
      },
      // Disconnecting from websocket on logout (MS)
      disconnectWebsocket() {
        if (this.stompClient) {
          this.stompClient.disconnect();
        }
        this.socketConnected = false;
      },
      // TODO: Implement error handler when BE is done
      subscribeErrors() {
        if (this.stompClient && this.stompClient.connected) {
          this.stompClient.subscribe(WS_ERRORS.replace(':coparentGuid', this.coparentGuid), tick => {
            console.log('I GET AN ERROR FROM WEBSOCKET CHANNEL')
            console.log(tick.body)
          });
        }
      },
      // Subscribing to error channel
      // Subscribing to conversations channel
      // Mapping intitial data from FP-APP (contacts) with conversations when response is list of contacts
      // Updating conversation when response is only one conversation
      subscribeWebsocket() {
        if (this.stompClient && this.stompClient.connected) {
          this.stompClient.send(WS_CONVERSATIONS_SEND.replace(':coparentGuid', this.coparentGuid));

          this.stompClient.subscribe(WS_CONVERSATIONS_SUBSCRIBE.replace(':coparentGuid', this.coparentGuid), tick => {
            let data = JSON.parse(tick.body);
            if (data.length >= 1) {
              let conversations = [];
              this.contactsList.forEach(contactIt => {
                let conversation = data.find(item => item.participantGuid === contactIt.coparentGuid);
                if (conversation) {
                  conversation = this.generateConversation(conversation, contactIt);
                  conversations.push(conversation);
                }
              })
              this.conversationsListMutation(conversations);
              this.$root.$emit('conversations', conversations);

              let subscribedConversations = [];
              conversations.forEach(item => {
                let subsrcibedConversation = {
                  conversationGuid: item.conversationGuid,
                  color: item.color,
                  subscribed: false
                }
                subscribedConversations.push(subsrcibedConversation)
              })
              this.setSubscribedConversationsListAction(subscribedConversations);
            }
            else if (data.conversationGuid) {
              let conversations = this.conversationsList;
              let conversationTemp = null;
              if (data.newConversation === true) {
                conversationTemp = this.conversationsList.find(item => item.participantGuid === data.participantGuid);
              } else if (data.newConversation === null) {
                conversationTemp = this.conversationsList.find(item => item.conversationGuid === data.conversationGuid);
              }

              this.contactsList.forEach(contactIt => {
                if (contactIt.coparentGuid === data.participantGuid) {
                  data = this.generateConversation(data, contactIt);
                }
              });

              let index = this.conversationsList.indexOf(conversationTemp);
              conversations[index] = data;
              this.conversationsListMutation(conversations);
              this.$root.$emit('conversations', conversations);
              if (data.newConversation === true) {
                this.$root.$emit('loadConversation', data);
              }

              let subscribedConversationsStore =  this.subscribedConversationsList;
              subscribedConversationsStore.forEach(item => {
                if (item.color === data.color && data.newConversation) {
                  item.subscribed = false
                  item.conversationGuid = data.conversationGuid
                }
              })
              this.setSubscribedConversationsListAction(subscribedConversationsStore);
            }
          });
        }
      },
      generateConversation(conversation, contact) {
        conversation.color = contact.color;
        conversation.firstName = contact.firstName;
        conversation.lastName = contact.lastName;
        conversation.fullName = contact.firstName + " " + contact.lastName;
        conversation.nameInitials = contact.firstName.charAt(0) + contact.lastName.charAt(0);
        return conversation
      },
      conversationsUpdated(conversations) {
        this.conversationsList = conversations;
      },
      gotItClicked() {
        const tooltipsList = [{uiId: 'PARENTING_CALENDAR', show: false}, {uiId: 'SCHEDULE_PAYMENT', show: false}];
        this.registrationProgresssGotItTooltipsAction(
          {
            tooltips: tooltipsList,
          }
        )
        .then(response => {
          this.getRegistrationProgressAction();
        })
      },
      /**
       * @description Connecting on fp-app websocket
       */
      connectFpAppWebsocket() {
        this.fpAppSocket = new SockJS(WS_APP);
        this.fpAppStompClient = Stomp.over(this.fpAppSocket);
        this.fpAppStompClient.debug = () => {};
        this.fpAppStompClient.connect({}, () => {
            this.$root.$emit('fp-app-websocket-connected')
          }
        );
      },
      /**
       * @description Disconnecting from fp-app websocket on logout
       */
      disconnectFpAppWebsocket() {
        if (this.fpAppStompClient) {
          this.fpAppStompClient.disconnect();
        }
      }
    }
  };
</script>

<style lang="scss">
  @import '@/scss/import-app-layout.scss';
</style>
